import { useMemo } from 'react';
import {
  PatientSearchFilters,
  SearchFilterType,
  type FilterConfigsMap,
} from '../types';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import {
  generateFilterOption,
  isOpenSafetyAlertsFilterKey,
  isReadyForReviewFilterKey,
} from '../utils';
import { useEnvironment } from '../../../../context';
import { SystemPermission } from '../../../../context/PermissionsContext';

export const useFilteredOptions = () => {
  const { env } = useEnvironment();
  const isDevelopment = env === 'development';
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const searchFiltersMap: FilterConfigsMap =
    selectedTenancy?.features?.searchFiltersMap || {};
  const canAccessOpenSafetyAlerts =
    !selectedTenancy?.permissionsSetup ||
    !!selectedTenancy?.userPermissions?.[SystemPermission.SAFETY_MONITORING];
  const canAccessReadyForReview =
    !selectedTenancy?.permissionsSetup ||
    !selectedTenancy?.userPermissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const availableOptions = useMemo(() => {
    const configuredOptions = Object.keys(searchFiltersMap)
      .filter(
        (key) =>
          (canAccessOpenSafetyAlerts || !isOpenSafetyAlertsFilterKey(key)) &&
          (canAccessReadyForReview || !isReadyForReviewFilterKey(key)),
      )
      .map((key) => {
        const backendData = searchFiltersMap[key];
        return generateFilterOption(backendData?.label ?? key, key, {
          filterKey: key,
          type:
            (backendData?.type as SearchFilterType) ||
            SearchFilterType.NO_SELECTION,
        });
      });

    const hasConfiguredOpenSafetyAlerts = Object.keys(searchFiltersMap).some(
      (key) => isOpenSafetyAlertsFilterKey(key),
    );

    if (canAccessOpenSafetyAlerts && !hasConfiguredOpenSafetyAlerts) {
      configuredOptions.push(
        generateFilterOption(
          PatientSearchFilters.MyPainHealthOpenSafetyAlerts,
          'MyPainHealthOpenSafetyAlerts',
          {
            filterKey: 'MyPainHealthOpenSafetyAlerts',
            type: SearchFilterType.NO_SELECTION,
          },
        ),
      );
    }

    return configuredOptions;
  }, [canAccessOpenSafetyAlerts, canAccessReadyForReview, searchFiltersMap]);

  return [
    isDevelopment
      ? generateFilterOption('All Patients', 'all-patient-filter', {
          type: SearchFilterType.NO_SELECTION,
        })
      : null,
    ...availableOptions,
  ].filter((item) => item !== null);
};
